import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import {EventConstants} from 'constants/EventConstants';
import {Batch, StorableSummary} from 'constants/SampleConstants';
import {EntitySummary} from 'constants/UserConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import AppDispatcher from 'lib/Dispatcher';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {modelFetched} from 'models/ManagedModelsReduxActions';
import {EntrySummary} from 'notebook/ELNConstants';
import {bulkResolveValidateSmilesToServerResolutionResult} from 'notebook/integrations/chemistry/SmilesValidationCustomEval';
import {bulkResolveLookupColumnToServerResolutionResult} from 'notebook/structured-tables/lookup-columns/LookupColumnResolver';
import {
  bulkResolveCustomSyntaxColumnToServerResolutionResult,
  bulkResolveHELMColumnToServerResolutionResult,
  bulkResolveIDTColumnToServerResolutionResult,
} from 'notebook/structured-tables/sequence-notations/ValidateNotationsResolver';
import {bulkResolveSnapshotFieldPreviewToServerResolutionResult} from 'notebook/structured-tables/snapshot-fields/SnapshotFieldPreviewResolver';
import {ResolutionQuery} from 'notebook/tables/excess/Resolver';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';

import {
  CustomResolutionQuery,
  CustomResolutionQueryType,
  CustomResolutionQueryTypesToResolutionFn,
  LookupColumnQuery,
  ServerResolutionResult,
  SnapshotFieldPreviewQuery,
  StringResolutionQuery,
  StringResolutionResult,
  StringResolutionType,
  ValidateCustomSyntaxQuery,
  ValidateHelmQuery,
  ValidateIDTQuery,
  ValidateSmilesQuery,
} from './StringResolutionConstants';
import {bulkResolveStringsToValues as bulkResolveStringsToValues1} from './StringResolutionDAO';

export enum StringResolutionActionType {
  BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_COMPLETED = 'BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_COMPLETED',
  BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_STARTED = 'BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_STARTED',
  BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_FAILED = 'BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_FAILED',
  CLEAR_STRING_RESOLUTION_CACHE = 'CLEAR_STRING_RESOLUTION_CACHE',
  SET_CACHED_STRING_RESOLUTIONS = 'SET_CACHED_STRING_RESOLUTIONS',
}

export interface BulkResolveStringsToValuesFetchCompletedAction {
  type: StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_COMPLETED;
  resolutionQueries: Array<ResolutionQuery>;
  resolutionResults: Array<StringResolutionResult>;
}

/**
 * resolutionQueries should correspond by index to resolutionResults.
 */
export function bulkResolveStringsToValuesFetchCompleted(
  resolutionQueries: Array<ResolutionQuery>,
  resolutionResults: Array<StringResolutionResult>
): BulkResolveStringsToValuesFetchCompletedAction {
  return {
    type: StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_COMPLETED,
    resolutionQueries,
    resolutionResults,
  };
}

export interface BulkResolveStringsToValuesFetchStartedAction {
  type: StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_STARTED;
  resolutionQueries: Array<ResolutionQuery>;
}

export function bulkResolveStringsToValuesFetchStarted(
  resolutionQueries: Array<ResolutionQuery>
): BulkResolveStringsToValuesFetchStartedAction {
  return {
    type: StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_STARTED,
    resolutionQueries,
  };
}

export interface BulkResolveStringsToValuesFetchFailedAction {
  type: StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_FAILED;
  resolutionQueries: Array<ResolutionQuery>;
}

export function bulkResolveStringsToValuesFetchFailed(
  resolutionQueries: Array<ResolutionQuery>
): BulkResolveStringsToValuesFetchFailedAction {
  return {
    type: StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_FAILED,
    resolutionQueries,
  };
}

export interface ClearStringResolutionCacheAction {
  type: StringResolutionActionType.CLEAR_STRING_RESOLUTION_CACHE;
}

export function clearStringResolutionCache(): ClearStringResolutionCacheAction {
  return {type: StringResolutionActionType.CLEAR_STRING_RESOLUTION_CACHE};
}

export interface SetCachedStringResolutions {
  type: StringResolutionActionType.SET_CACHED_STRING_RESOLUTIONS;
  resolutionQueries: Array<ResolutionQuery>;
  resolutionResults: Array<StringResolutionResult>;
}

export function setCachedStringResolutions(
  resolutionQueries: Array<ResolutionQuery>,
  resolutionResults: Array<StringResolutionResult>
): SetCachedStringResolutions {
  return {
    type: StringResolutionActionType.SET_CACHED_STRING_RESOLUTIONS,
    resolutionQueries,
    resolutionResults,
  };
}

function getResolutionResultsFromServerResponse(
  resolutionResultsWithModels: Array<ServerResolutionResult>
): Array<StringResolutionResult> {
  return resolutionResultsWithModels.map((serverResolutionResult) => {
    const resolutionResult: StringResolutionResult = {
      error: serverResolutionResult.error,
      resolvedValue: serverResolutionResult.resolvedValue,
    };
    if (serverResolutionResult.error && serverResolutionResult.isWarning != null) {
      resolutionResult.isWarning = serverResolutionResult.isWarning;
    }
    return resolutionResult;
  });
}

function dispatchForResolutionResults(resolutionResultsWithModels: Array<ServerResolutionResult>): void {
  const bioentitySummariesById: {[bioentityId: string]: BioentitySummary} = {};
  const batchesById: {[batchId: string]: Batch} = {};
  const storableSummariesById: {[storableId: string]: StorableSummary} = {};
  const entrySummariesById: {[entryId: string]: EntrySummary} = {};
  const entitySummariesByHandle: {[handle: string]: EntitySummary} = {};

  for (const resolutionResult of resolutionResultsWithModels) {
    if (resolutionResult.error) {
      continue;
    }

    switch (resolutionResult.type) {
      case StringResolutionType.BIOENTITY:
        for (const bioentitySummary of resolutionResult.bioentitySummaries) {
          bioentitySummariesById[bioentitySummary.id] = bioentitySummary;
        }
        break;
      case StringResolutionType.BATCH:
        for (const batch of resolutionResult.batches) {
          batchesById[batch.id] = batch;
        }
        break;
      case StringResolutionType.DROPDOWN:
        break;
      case StringResolutionType.ENTRY:
        for (const entrySummary of resolutionResult.entrySummaries) {
          entrySummariesById[entrySummary.id] = entrySummary;
        }
        break;
      case StringResolutionType.STORABLE:
        for (const storableSummary of resolutionResult.storableSummaries) {
          storableSummariesById[storableSummary.id] = storableSummary;
        }
        break;
      case StringResolutionType.USER:
        for (const user of resolutionResult.entitySummaries) {
          entitySummariesByHandle[user.handle] = user;
        }
        break;
      case StringResolutionType.PARTY:
      case StringResolutionType.PROCEDURE_CONDITION_REPLICATES:
      case StringResolutionType.PROCESS_TASK:
      case StringResolutionType.CUSTOM:
      case StringResolutionType.STUDY:
      case StringResolutionType.UNIT_REFERENCE:
        break;
      default:
        assertAndContinue(false, `Invalid string resolution type ${resolutionResult.type}`);
    }
  }

  if (!_.isEmpty(bioentitySummariesById)) {
    getStore().dispatch(ModelActions.filesFetchCompleted(bioentitySummariesById));
  }

  if (!_.isEmpty(batchesById)) {
    AppDispatcher.dispatch({
      actionType: EventConstants.BATCHES_BULK_FETCH_COMPLETED,
      batchesById,
    });
  }

  if (!_.isEmpty(storableSummariesById)) {
    AppDispatcher.dispatch({
      actionType: EventConstants.STORABLE_SUMMARIES_FETCH_COMPLETED,
      storables: Object.values(storableSummariesById),
    });
  }

  if (!_.isEmpty(entrySummariesById)) {
    getStore().dispatch(ModelActions.entriesFetchCompleted(entrySummariesById));
  }

  if (!_.isEmpty(entitySummariesByHandle)) {
    getStore().dispatch(modelFetched(ManagedModelType.ENTITY_SUMMARY_BY_HANDLE, entitySummariesByHandle));
  }
}

export async function bulkResolveStringsToValues(
  stringResolutionQueries: Array<StringResolutionQuery>
): Promise<void> {
  if (stringResolutionQueries.length === 0) {
    return;
  }
  getStore().dispatch(bulkResolveStringsToValuesFetchStarted(stringResolutionQueries));
  let resolutionResultsWithModels: Array<ServerResolutionResult> = [];
  try {
    ({resolutionResultsWithModels} = await bulkResolveStringsToValues1(stringResolutionQueries));
  } catch (e) {
    getStore().dispatch(bulkResolveStringsToValuesFetchFailed(stringResolutionQueries));
    throw e;
  }

  const resolutionResults = getResolutionResultsFromServerResponse(resolutionResultsWithModels);
  dispatchForResolutionResults(resolutionResultsWithModels);
  getStore().dispatch(bulkResolveStringsToValuesFetchCompleted(stringResolutionQueries, resolutionResults));
}

interface BatchedQueriesWithPromise {
  customResolutionQueries: Array<CustomResolutionQuery>;
  resolutionPromise: Promise<ServerResolutionResult | Array<ServerResolutionResult>>;
}

function addLookupColumnQueryToBatch(
  query: LookupColumnQuery,
  lookupQueriesByLookupConfigId: {[lookupConfigId: string]: Array<LookupColumnQuery>}
): void {
  if (!lookupQueriesByLookupConfigId.hasOwnProperty(query.lookupConfigId)) {
    lookupQueriesByLookupConfigId[query.lookupConfigId] = [query];
  } else {
    lookupQueriesByLookupConfigId[query.lookupConfigId].push(query);
  }
}

function addSnapshotFieldPreviewQueryToBatch(
  query: SnapshotFieldPreviewQuery,
  snapshotFieldPreviewQueriesBySnapshotFieldId: {[snapshotFieldId: string]: Array<SnapshotFieldPreviewQuery>}
): void {
  if (!snapshotFieldPreviewQueriesBySnapshotFieldId.hasOwnProperty(query.snapshotFieldId)) {
    snapshotFieldPreviewQueriesBySnapshotFieldId[query.snapshotFieldId] = [query];
  } else {
    snapshotFieldPreviewQueriesBySnapshotFieldId[query.snapshotFieldId].push(query);
  }
}

function addValidateCustomSyntaxQueryToBatch(
  query: ValidateCustomSyntaxQuery,
  validateCustomSyntaxQueriesByType: {[batchId: string]: Array<ValidateCustomSyntaxQuery>}
): void {
  const batchId = `${query.nucleotideType}-${query.customSyntaxName}`;
  if (!validateCustomSyntaxQueriesByType.hasOwnProperty(batchId)) {
    validateCustomSyntaxQueriesByType[batchId] = [query];
  } else {
    validateCustomSyntaxQueriesByType[batchId].push(query);
  }
}

export async function bulkResolveCustomResolutionQueries(
  customResolutionQueries: Array<CustomResolutionQuery>
): Promise<void> {
  if (customResolutionQueries.length === 0) {
    return;
  }
  getStore().dispatch(bulkResolveStringsToValuesFetchStarted(customResolutionQueries));

  // Run each query. If a query does fetching, the fetch must be auto-batched.
  // Otherwise, a large table could send out hundreds of HTTP requests!
  //
  // To get autobatching, you can use GraphQL, or collect the related queries and pass them all together,
  // similar to how stringResolution does bulkResolution
  const batchedQueries: Array<BatchedQueriesWithPromise> = [];
  const lookupQueriesByLookupConfigId: {
    [lookupConfigId: string]: Array<LookupColumnQuery>;
  } = {};
  const snapshotFieldPreviewQueriesBySnapshotFieldId: {
    [snapshotFieldId: string]: Array<SnapshotFieldPreviewQuery>;
  } = {};
  const validateSmilesQueries: Array<ValidateSmilesQuery> = [];
  const validateHelmQueriesByType: {
    [nucleotideType: string]: Array<ValidateHelmQuery>;
  } = {[NucleotideType.RNA]: [], [NucleotideType.DNA]: []};
  const validateIDTQueriesByType: {
    [nucleotideType: string]: Array<ValidateIDTQuery>;
  } = {[NucleotideType.RNA]: [], [NucleotideType.DNA]: []};
  const validateCustomSyntaxQueriesByType: {[batchId: string]: Array<ValidateCustomSyntaxQuery>} = {};

  for (const query of customResolutionQueries) {
    if (CustomResolutionQueryTypesToResolutionFn.hasOwnProperty(query.customResolutionQueryType)) {
      batchedQueries.push({
        customResolutionQueries: [query],
        resolutionPromise: CustomResolutionQueryTypesToResolutionFn[query.customResolutionQueryType](query),
      });
    } else if (query.customResolutionQueryType === CustomResolutionQueryType.LOOKUP_COLUMN) {
      addLookupColumnQueryToBatch(query, lookupQueriesByLookupConfigId);
    } else if (query.customResolutionQueryType === CustomResolutionQueryType.SNAPSHOT_FIELD_PREVIEW) {
      addSnapshotFieldPreviewQueryToBatch(query, snapshotFieldPreviewQueriesBySnapshotFieldId);
    } else if (
      query.customResolutionQueryType === CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_SMILES
    ) {
      validateSmilesQueries.push(query);
    } else if (
      query.customResolutionQueryType === CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_HELM
    ) {
      validateHelmQueriesByType[query.nucleotideType].push(query);
    } else if (
      query.customResolutionQueryType === CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_IDT
    ) {
      validateIDTQueriesByType[query.nucleotideType].push(query);
    } else if (
      query.customResolutionQueryType === CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_CUSTOM_SYNTAX
    ) {
      addValidateCustomSyntaxQueryToBatch(query, validateCustomSyntaxQueriesByType);
    } else {
      throw new Error(`Unexpected query type ${query.customResolutionQueryType}`);
    }
  }

  for (const lookupQueries of Object.values(lookupQueriesByLookupConfigId)) {
    batchedQueries.push({
      customResolutionQueries: lookupQueries,
      resolutionPromise: bulkResolveLookupColumnToServerResolutionResult(lookupQueries),
    });
  }

  for (const queries of Object.values(snapshotFieldPreviewQueriesBySnapshotFieldId)) {
    batchedQueries.push({
      customResolutionQueries: queries,
      resolutionPromise: bulkResolveSnapshotFieldPreviewToServerResolutionResult(queries),
    });
  }

  if (validateSmilesQueries.length > 0) {
    batchedQueries.push({
      customResolutionQueries: validateSmilesQueries,
      resolutionPromise: bulkResolveValidateSmilesToServerResolutionResult(validateSmilesQueries),
    });
  }

  for (const queries of Object.values(validateHelmQueriesByType)) {
    if (queries.length > 0) {
      batchedQueries.push({
        customResolutionQueries: queries,
        resolutionPromise: bulkResolveHELMColumnToServerResolutionResult(queries),
      });
    }
  }

  for (const queries of Object.values(validateIDTQueriesByType)) {
    if (queries.length > 0) {
      batchedQueries.push({
        customResolutionQueries: queries,
        resolutionPromise: bulkResolveIDTColumnToServerResolutionResult(queries),
      });
    }
  }

  for (const queries of Object.values(validateCustomSyntaxQueriesByType)) {
    if (queries.length > 0) {
      batchedQueries.push({
        customResolutionQueries: queries,
        resolutionPromise: bulkResolveCustomSyntaxColumnToServerResolutionResult(queries),
      });
    }
  }

  let resolutionResultsWithModels: Array<ServerResolutionResult> = [];
  try {
    for (const resolutionResult of await Promise.all(batchedQueries.map((q) => q.resolutionPromise))) {
      if (Array.isArray(resolutionResult)) {
        resolutionResultsWithModels = resolutionResultsWithModels.concat(resolutionResult);
      } else {
        resolutionResultsWithModels.push(resolutionResult);
      }
    }
  } catch (e) {
    getStore().dispatch(bulkResolveStringsToValuesFetchFailed(customResolutionQueries));
    throw e;
  }

  const resolutionResults = getResolutionResultsFromServerResponse(resolutionResultsWithModels);
  dispatchForResolutionResults(resolutionResultsWithModels);
  getStore().dispatch(
    bulkResolveStringsToValuesFetchCompleted(
      batchedQueries.flatMap((q) => q.customResolutionQueries),
      resolutionResults
    )
  );
}

export function clearCache(): void {
  getStore().dispatch(clearStringResolutionCache());
}

function isMatchingLookupColumnQuery(
  lookupConfigIds: Array<string>,
  resolutionQuery: CustomResolutionQuery
): boolean {
  return (
    resolutionQuery.customResolutionQueryType === CustomResolutionQueryType.LOOKUP_COLUMN &&
    lookupConfigIds.includes(resolutionQuery.lookupConfigId)
  );
}

function isMatchingInventoryQuantityColumnQuery(
  ids: Array<string>,
  resolutionQuery: CustomResolutionQuery
): boolean {
  if (
    // type narrowing to use `containerIds`
    resolutionQuery.customResolutionQueryType ===
    CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_CONTAINER
  ) {
    return ids.includes(resolutionQuery.containerId);
  } else if (
    // type narrowing to use `plateIds`
    resolutionQuery.customResolutionQueryType ===
    CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_PLATE
  ) {
    return ids.includes(resolutionQuery.plateId);
  }
  return false;
}

/**
 * Clear cache lines by getting the redux state, filtering out matching items, and setting the store state
 * with the reduced list. Matching items are determined by comparing customResolutionQueryType and the ids
 * that are passed in. Custom match functions are used as the id key varies on each query.
 * @param {CustomResolutionQueryType} customResolutionQueryType
 * @param {Array<string>} ids
 */
export function clearResolutionResultsByTypeAndId(
  customResolutionQueryType: CustomResolutionQueryType,
  ids: Array<string>
): void {
  const elnState = getStore().getState().models.eln;
  const filteredResolutionQueries: Array<ResolutionQuery> = [];
  const filteredResolutionResults: Array<StringResolutionResult> = [];
  Object.keys(elnState.resolutionResultsByResolutionQuery).forEach((modelKey) => {
    const resolutionQuery: ResolutionQuery = JSON.parse(modelKey);
    if (
      resolutionQuery.type === ResolutionQueryType.CUSTOM &&
      customResolutionQueryType === resolutionQuery.customResolutionQueryType &&
      // Once we've type narrowed and checked that the resolution query matched our expected type
      // we can check for ids
      !isMatchingLookupColumnQuery(ids, resolutionQuery) &&
      !isMatchingInventoryQuantityColumnQuery(ids, resolutionQuery)
    ) {
      // since the resolution query did not match any ids, add it to the filtered list to keep it in the cache
      filteredResolutionQueries.push(resolutionQuery);
      filteredResolutionResults.push(elnState.resolutionResultsByResolutionQuery[modelKey]);
    }
  });
  getStore().dispatch(setCachedStringResolutions(filteredResolutionQueries, filteredResolutionResults));
}

export type BulkResolveStringsToValuesAction =
  | BulkResolveStringsToValuesFetchCompletedAction
  | BulkResolveStringsToValuesFetchStartedAction
  | BulkResolveStringsToValuesFetchFailedAction
  | ClearStringResolutionCacheAction
  | SetCachedStringResolutions;
